import './Tooltip.css'
import { useRef, useEffect, useCallback, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { flushSync } from 'react-dom'
import {
  createTooltip,
  positionAndShowTooltip,
  scheduleTooltipHide,
} from './tooltipManager'
import type { Placement } from '@floating-ui/dom'

type Props = {
  placement?: Placement
  disabled?: boolean
  children?: ReactNode
}

export function Tooltip({
  placement = 'top',
  disabled,
  children,
}: Props) {
  const spanRef = useRef<HTMLSpanElement>(null)
  const currentRef = useRef<import('./tooltipManager').TooltipState | null>(null)
  const stopPositionLoopRef = useRef<(() => void) | null>(null)

  const show = useCallback(async () => {
    const trigger = spanRef.current?.parentElement
    if (!trigger) return
    const rootRef = { current: null as ReturnType<typeof createRoot> | null }
    const {
      tooltipEl,
      contentEl,
      arrowEl,
    } = createTooltip(() => rootRef.current?.unmount())
    const root = createRoot(contentEl)
    rootRef.current = root
    const state = {
      element: tooltipEl,
      onUnmount: () => root.unmount(),
    }
    currentRef.current = state
    flushSync(() => {
      root.render(children)
    })
    const stopPositionLoop = await positionAndShowTooltip(trigger, tooltipEl, arrowEl, placement)
    stopPositionLoopRef.current = stopPositionLoop
    trigger.setAttribute('aria-describedby', tooltipEl.id)
  }, [children, placement])

  const scheduleHide = useCallback(() => {
    const trigger = spanRef.current?.parentElement
    const state = currentRef.current
    if (!state) return
    currentRef.current = null
    stopPositionLoopRef.current?.()
    stopPositionLoopRef.current = null
    if (trigger) trigger.removeAttribute('aria-describedby')
    scheduleTooltipHide(state, 120)
  }, [])

  const handleFocusIn = useCallback(
    (e: FocusEvent) => {
      const target = e.target as Element
      if (target.matches(':focus-visible')) show()
    },
    [show],
  )

  useEffect(() => {
    if (disabled) return
    const trigger = spanRef.current?.parentElement
    if (!trigger) return
    trigger.addEventListener('mouseenter', show)
    trigger.addEventListener('mouseleave', scheduleHide)
    trigger.addEventListener('focusin', handleFocusIn)
    trigger.addEventListener('focusout', scheduleHide)
    return () => {
      trigger.removeEventListener('mouseenter', show)
      trigger.removeEventListener('mouseleave', scheduleHide)
      trigger.removeEventListener('focusin', handleFocusIn)
      trigger.removeEventListener('focusout', scheduleHide)
    }
  }, [show, disabled, scheduleHide, handleFocusIn])

  if (!children) return undefined
  if (disabled) return undefined

  return <span ref={spanRef} className='TooltipHandle' aria-hidden />
}
